import { EditOutlined, FileTextOutlined, ToolOutlined } from '@ant-design/icons';
import { Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import type { WorkflowStep } from '@/api/workflows';

const { Title, Paragraph, Text } = Typography;

/**
 * Read-only placeholder for step types that don't yet have a runtime UI:
 *   - PdfFileUpload    → originator-side concern, advisor sees a note that
 *                        these PDFs are configured upstream
 *   - OneSpanESign     → e-signature integration deferred
 *   - PdfGeneration    → final-document download list (built post-submit)
 *
 * Each surfaces the step description + a "coming soon" message. The shell
 * still navigates through them so the advisor experiences the full flow.
 */
export function PlaceholderStep({ step, language }: { step: WorkflowStep; language: 'en' | 'fr' }) {
  const { t } = useTranslation();
  const description = language === 'fr' ? step.descriptionFr : step.descriptionEn;

  const icon = (() => {
    if (step.type === 'OneSpanESign')
      return <EditOutlined style={{ fontSize: 40, color: '#0179C1' }} />;
    if (step.type === 'PdfGeneration')
      return <FileTextOutlined style={{ fontSize: 40, color: '#0179C1' }} />;
    return <ToolOutlined style={{ fontSize: 40, color: '#0179C1' }} />;
  })();

  const noteKey = (() => {
    if (step.type === 'OneSpanESign') return 'formRenderer.placeholder.oneSpanNote';
    if (step.type === 'PdfGeneration') return 'formRenderer.placeholder.pdfGenerationNote';
    if (step.type === 'PdfFileUpload') return 'formRenderer.placeholder.pdfTemplatesNote';
    return 'formRenderer.placeholder.genericNote';
  })();

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 16,
      }}
    >
      {description && (
        <Paragraph style={{ marginBottom: 0, color: '#374151', fontSize: 14 }}>
          {description}
        </Paragraph>
      )}
      <div
        style={{
          alignSelf: 'stretch',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 12,
          padding: '36px 24px',
          background: '#f8fafc',
          border: '1px dashed #cbd5e1',
          borderRadius: 12,
          textAlign: 'center',
        }}
      >
        {icon}
        <Title level={4} style={{ margin: 0, color: '#111827' }}>
          {t('formRenderer.placeholder.comingSoonTitle')}
        </Title>
        <Text style={{ color: '#475569', maxWidth: 480 }}>{t(noteKey)}</Text>
      </div>
    </div>
  );
}
